import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { obtenerBiblioteca, type Manga } from '../api/consultas'
import Finalizar, { useGuardarFinalizada } from '../componentes/Finalizar'
import Portada from '../componentes/Portada'
import { comoDia } from '../lib/fechas'
import { comoDuracion, fechaDeFinal, leerFinalizada, type Finalizada } from '../lib/finalizadas'
import { useMantenerPulsado } from '../lib/pulsacion'

interface Entrada {
  manga: Manga
  datos: Finalizada
}

function Estrellas({ valor, alElegir }: { valor: number; alElegir: (n: number) => void }) {
  return (
    <div className="estrellas">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          className={n <= valor ? 'puesta' : undefined}
          title={n === valor ? 'Tocar para quitar la puntuacion' : `${n} de 5`}
          onClick={() => alElegir(n === valor ? 0 : n)}
        >
          {n <= valor ? '★' : '☆'}
        </button>
      ))}
    </div>
  )
}

/** Una serie del estante. Es un componente aparte porque cada fila necesita
 *  su propio gesto de mantener pulsado, y los hooks no van dentro de un map. */
function Fila({ entrada, alEditar }: { entrada: Entrada; alEditar: () => void }) {
  const { manga, datos } = entrada
  const guardar = useGuardarFinalizada()
  const pulsar = useMantenerPulsado(alEditar)

  const abandonada = datos.estado === 'abandonada'
  const duracion = comoDuracion(datos)

  /*
   * El dia que la empezaste: la lectura mas antigua que guarda el servidor.
   * Se calla cuando no hay fecha —hay fuentes que nunca guardaron
   * `lastReadAt`— y cuando cae el mismo dia que el final, que entonces la
   * linea seria "empezada el 15 · terminada el 15" y no dice nada.
   */
  const empezada =
    datos.inicio && new Date(datos.inicio).toDateString() !== new Date(fechaDeFinal(datos)).toDateString()
      ? comoDia(datos.inicio)
      : ''

  const segunda = abandonada
    ? `La dejaste por el capitulo ${datos.leidos}${datos.total ? ` de ${datos.total}` : ''}`
    : [
        `${datos.leidos} ${datos.leidos === 1 ? 'capitulo' : 'capitulos'}`,
        duracion && `la leiste en ${duracion}`,
      ]
        .filter(Boolean)
        .join(' · ')

  return (
    <li className="finalizada">
      <Link to={`/manga/${manga.id}`} className="finalizada-portada" {...pulsar}>
        <Portada mangaId={manga.id} ruta={manga.thumbnailUrl} titulo={manga.title} />
      </Link>

      <div className="finalizada-datos">
        <Link to={`/manga/${manga.id}`} className="finalizada-titulo" {...pulsar}>
          {manga.title}
        </Link>
        <span className="meta">
          {abandonada ? '🚪 Abandonada' : '✓ Terminada'} el {comoDia(fechaDeFinal(datos))}
        </span>
        {empezada && (
          <span className="meta" title="La lectura mas antigua que guarda el servidor">
            Empezada el {empezada}
          </span>
        )}
        {segunda && <span className="meta">{segunda}</span>}
        <Estrellas
          valor={datos.estrellas}
          alElegir={(n) => guardar.mutate({ mangaId: manga.id, datos: { ...datos, estrellas: n } })}
        />
        {datos.nota && <p className="opinion">{datos.nota}</p>}
      </div>

      <button className="icono-accion" title="Puntuacion, nota y mas opciones" onClick={alEditar}>
        ✎
      </button>
    </li>
  )
}

/**
 * El estante: lo que ya terminaste o dejaste.
 *
 * No es una lista de archivo muerto, es la parte de la biblioteca que se mira
 * con gusto. Por eso lleva las fechas, cuanto te duro cada serie y sitio para
 * puntuarla: son datos que el servidor ya tenia y que nadie habia sacado a la
 * pantalla.
 *
 * Va por ano y de lo mas reciente a lo mas viejo, que es como se recuerda.
 */
export default function Finalizadas() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['biblioteca'],
    queryFn: obtenerBiblioteca,
  })

  const [editando, setEditando] = useState<number | null>(null)

  const entradas = useMemo<Entrada[]>(() => {
    const lista: Entrada[] = []
    for (const manga of data ?? []) {
      const datos = leerFinalizada(manga.meta)
      if (datos) lista.push({ manga, datos })
    }
    return lista.sort((a, b) => fechaDeFinal(b.datos) - fechaDeFinal(a.datos))
  }, [data])

  const grupos = useMemo(() => {
    const porAno: { ano: number; series: Entrada[] }[] = []
    for (const entrada of entradas) {
      const ano = new Date(fechaDeFinal(entrada.datos)).getFullYear()
      const ultimo = porAno[porAno.length - 1]
      if (ultimo && ultimo.ano === ano) ultimo.series.push(entrada)
      else porAno.push({ ano, series: [entrada] })
    }
    return porAno
  }, [entradas])

  const capitulos = entradas.reduce((suma, e) => suma + e.datos.leidos, 0)
  const esteAno = entradas.filter(
    (e) => new Date(fechaDeFinal(e.datos)).getFullYear() === new Date().getFullYear(),
  ).length

  const enEdicion = entradas.find((e) => e.manga.id === editando)

  return (
    <>
      <button className="volver" onClick={() => history.back()}>
        ‹ Biblioteca
      </button>

      <div className="cabecera-lista">
        <h1>Finalizadas</h1>
      </div>

      <p className="subtitulo">
        {entradas.length === 0
          ? 'Lo que termines aparecera aqui'
          : [
              `${entradas.length} ${entradas.length === 1 ? 'serie' : 'series'}`,
              `${capitulos.toLocaleString('es-ES')} capitulos`,
              esteAno > 0 && `${esteAno} este ano`,
            ]
              .filter(Boolean)
              .join(' · ')}
      </p>

      {isLoading && <p className="cargando">Cargando…</p>}

      {error && (
        <div className="aviso error">
          <strong>No se pudo cargar</strong>
          <p>{(error as Error).message}</p>
        </div>
      )}

      {!isLoading && entradas.length === 0 && (
        <div className="aviso">
          <strong>Todavia no has terminado ninguna</strong>
          <p style={{ fontSize: 13 }}>
            En la <Link to="/">biblioteca</Link>, manten pulsada una portada —o click derecho en el
            PC— y elige <strong>Marcar como finalizada</strong>. La serie sale de la rejilla y viene
            aqui con la fecha, lo que te duro y sitio para puntuarla.
          </p>
        </div>
      )}

      {grupos.map((grupo) => (
        <section key={grupo.ano}>
          <h2 className="ano-estante">{grupo.ano}</h2>
          <ul className="estante">
            {grupo.series.map((entrada) => (
              <Fila
                key={entrada.manga.id}
                entrada={entrada}
                alEditar={() => setEditando(entrada.manga.id)}
              />
            ))}
          </ul>
        </section>
      ))}

      {enEdicion && (
        <Finalizar
          mangaId={enEdicion.manga.id}
          titulo={enEdicion.manga.title}
          finalizada={enEdicion.datos}
          conNota
          alCerrar={() => setEditando(null)}
        />
      )}
    </>
  )
}
